import { Typography } from "@mui/material";
import { IconArrowRight } from "@tabler/icons-react";
import React from "react";
import {
  DRAFT_STAGE_CHANGE_ERROR_MAX_DISPLAYED_APPLICATION,
  EXPERT_SKILL_NAME,
  MAX_APPLICATIONS_CHAR_LENGTH_TO_SHOW,
  SKILL_LEVELS,
} from "store/constant";
import { ttaiFormatDate } from "./datepicker-util/converters/date";
import { getDefineds } from "./datepicker-util/app-constants/DateDefineds";
import { startOfDay } from "date-fns";


export const changeSkillValueInCategory = (skillLevel) => {
  const skillCategory = SKILL_LEVELS.find(
    (level) => skillLevel <= level.skillValue
  );
  return skillCategory ? skillCategory.skillName : EXPERT_SKILL_NAME;
};

const applicantRespRemovableProperties = [
  "id",
  "recruiter",
  "appliedJob",
  "match",
  "match_analytic_count",
  "documents_count",
  "categ_ids",
  "applicant_ref_id",
  "priority",
  "medium_id",
  "source_id",
  "stage_id",
  "application_skills",
  "application_certifications",
  "response_status",
  "analytic_status",
  "create_date",
  "write_date",
];

export const createUpdateBody = (respObj) => {
  const filteredObj = Object.keys(respObj)
    .filter((key) => !applicantRespRemovableProperties.includes(key))
    .reduce((obj, key) => {
      obj[key] = respObj[key];
      return obj;
    }, {});

  return filteredObj;
};

export const createDeleteDialogData = (
  title,
  deleteItemName,
  deleteItemFrom,
  deleteItemFromName
) => {
  let deleteDialog = {
    title: title || "",

    content: (
      <React.Fragment>
        <Typography variant="hdDialogMsg">
          <Typography variant="hdDialogMsgStrong">{deleteItemName}</Typography>
          {""} will be deleted from {deleteItemFrom}{" "}
          <Typography variant="hdDialogMsgStrong">
            {deleteItemFromName}
          </Typography>
        </Typography>
      </React.Fragment>
    ),
  };

  return deleteDialog;
};

export const areDatesEqual = (date1, date2) => {
  const formDate1 = new Date(date1);
  const formDate2 = new Date(date2);

  if (formDate1 === null && formDate2 === null) {
    return true;
  }

  if (
    formDate1 === null ||
    formDate2 === null ||
    formDate1 === undefined ||
    formDate1.getFullYear() !== formDate2.getFullYear() ||
    formDate1.getMonth() !== formDate2.getMonth() ||
    formDate1.getDate() !== formDate2.getDate()
  ) {
    return false;
  }
  return true;
};

const checkChangedValue = (
  oldChar,
  oldFloat,
  oldDate,
  newChar,
  newFloat,
  newDate
) => {
  if (newChar === null && newDate === null && newFloat > 0) {
    return [oldFloat, newFloat];
  } else if (newDate === null && newFloat === 0.0 && newChar !== null) {
    return [oldChar, newChar];
  } else if (newChar === null && newFloat === 0.0 && newDate !== null) {
    return [
      oldDate === null ? "" : ttaiFormatDate(new Date(oldDate), "dd-MM-yyyy"),
      ttaiFormatDate(new Date(newDate), "dd-MM-yyyy"),
    ];
  } else {
    return ["", ""];
  }
};

const createMessageBasedOnOldAndNewValues = (updateObj) => {
  let values = {
    oldValue: "",
    newValue: "",
  };
  if (Object.keys(updateObj).length !== 0) {
    const updatedValues = checkChangedValue(
      updateObj?.old_value_char,
      updateObj?.old_value_float,
      updateObj?.old_value_datetime,
      updateObj?.new_value_char,
      updateObj?.new_value_float,
      updateObj?.new_value_datetime
    );

    values.oldValue = updatedValues[0];
    values.newValue = updatedValues[1];
    return values;
  } else {
    values.oldValue = "";
    values.newValue = "";
    return values;
  }
};

export const generateUpdateMessage = (updateObj) => {
  const description = updateObj?.description;
  const message = createMessageBasedOnOldAndNewValues(updateObj);
  return (
    <React.Fragment>
      <Typography variant="subtitle1">
        {description} : {message.oldValue}{" "}
        <IconArrowRight style={{ marginBottom: -4 }} size={18} />{" "}
        {message.newValue}
      </Typography>
    </React.Fragment>
  );
};

export const createClearDialogData = (
  title,
  clearItemName,
  clearItemFrom,
  clearItemFromName
) => {
  let deleteDialog = {
    title: title || "",

    content: (
      <React.Fragment>
        <Typography variant="hdDialogMsg">
          <Typography variant="hdDialogMsgStrong">{clearItemName}</Typography>
          {""} will be cleared from {clearItemFrom}{" "}
          <Typography variant="hdDialogMsgStrong">
            {clearItemFromName}
          </Typography>
        </Typography>
      </React.Fragment>
    ),
  };

  return deleteDialog;
};

export const createDraftApplicationsStageErrorMsg = (draftApplications) => {
  let message;
  let additionalCount;
  if (draftApplications.length === 0) {
    message = "No draft applications.";
  } else if (draftApplications.length === 1) {
    message = `${draftApplications[0]} is in Draft State, Stage can't be changed`;
  } else {
    let displayedApplications = draftApplications
      .sort()
      .slice(0, DRAFT_STAGE_CHANGE_ERROR_MAX_DISPLAYED_APPLICATION)
      .join(", ");

    let finalDisplayedApplications = displayedApplications;

    if (displayedApplications.length > MAX_APPLICATIONS_CHAR_LENGTH_TO_SHOW) {
      const lastIndexOfApplication = displayedApplications
        .slice(0, MAX_APPLICATIONS_CHAR_LENGTH_TO_SHOW)
        .lastIndexOf(",");
      finalDisplayedApplications = displayedApplications.slice(
        0,
        lastIndexOfApplication
      );

      const finalDisplayedApplicationsCount =
        finalDisplayedApplications.split(",").length;
      additionalCount =
        draftApplications.length - finalDisplayedApplicationsCount;
    } else {
      additionalCount =
        draftApplications.length -
        DRAFT_STAGE_CHANGE_ERROR_MAX_DISPLAYED_APPLICATION;
    }

    if (additionalCount > 0) {
      finalDisplayedApplications += ` and ${additionalCount} more application${
        additionalCount > 1 ? "s" : ""
      }`;
    }

    message = `${finalDisplayedApplications} ${
      draftApplications.length >
      DRAFT_STAGE_CHANGE_ERROR_MAX_DISPLAYED_APPLICATION
        ? `are in Draft State, Stage can't be changed`
        : `are in Draft State, Stage can't be changed`
    }`;
  }
  return message;
};

export const getComparisonOperator = (value) => {
  return value === true ? ">" : "<";
};

// const defineds = getDefineds();
// export const defaultSelectedFromDate = () => {
//   const defineds = getDefineds();
//   return defineds.startOfLast30Days;
// };

// export const defaultSelectedToDate = () => {
//   const defineds = getDefineds();
//   console.log("defineds.startOfToday", defineds.startOfToday)
//   return defineds.startOfToday;
// };

export const defaultSelectedFromDate = () => {
  return startOfDay(getDefineds().startOfLast30Days).toISOString();
};

export const defaultSelectedToDate = () => {
  return startOfDay(getDefineds().startOfToday).toISOString();
};